import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AccountsTable from './components/AccountsTable';
import AccountsFilters from './components/AccountsFilters';
import AccountDrawer from './components/AccountDrawer';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const AccountsPage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ type: '', industry: '', status: '' });
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newAccount, setNewAccount] = useState({ name: '', industry: '', website: '', phone: '', address: '' });
  const [saving, setSaving] = useState(false);
  const { profile } = useAuth();

  const fetchAccounts = useCallback(async () => {
    if (!profile?.org_id) return;
    setLoading(true);
    setError('');
    try {
      let query = supabase?.from('accounts')?.select('*, owner:profiles(full_name)')?.eq('org_id', profile?.org_id)?.is('deleted_at', null)?.order('created_at', { ascending: false });

      if (searchTerm) query = query?.ilike('name', `%${searchTerm}%`);
      if (filters?.industry) query = query?.eq('industry', filters?.industry);
      if (filters?.type) query = query?.eq('type', filters?.type);

      const { data, error: fetchError } = await query;
      if (fetchError) throw fetchError;
      setAccounts(data || []);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  }, [profile?.org_id, searchTerm, filters]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const handleSidebarToggle = () => setIsSidebarOpen(!isSidebarOpen);
  const handleSidebarClose = () => setIsSidebarOpen(false);

  const handleRowClick = (account) => {
    setSelectedAccount(account);
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedAccount(null);
  };

  const handleFiltersChange = (newFilters) => setFilters(newFilters);

  const handleAddAccount = async () => {
    if (!newAccount?.name?.trim()) return;
    setSaving(true);
    try {
      const { error: insertError } = await supabase?.from('accounts')?.insert({ ...newAccount, org_id: profile?.org_id, owner_id: profile?.id });
      if (insertError) throw insertError;
      setNewAccount({ name: '', industry: '', website: '', phone: '', address: '' });
      setIsAddModalOpen(false);
      fetchAccounts();
    } catch (err) {
      setError(err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async (accountId) => {
    if (!window.confirm('Move this account to trash?')) return;
    try {
      await supabase?.from('accounts')?.update({ deleted_at: new Date()?.toISOString() })?.eq('id', accountId);
      toast?.success('Moved to Trash');
      fetchAccounts();
      if (selectedAccount?.id === accountId) handleDrawerClose();
    } catch (err) {
      setError(err?.message);
    }
  };

  return (
    <>
      <Helmet>
        <title>Accounts - Wilcart CRM</title>
        <meta name="description" content="Manage your customer accounts in Wilcart CRM." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header onMenuToggle={handleSidebarToggle} isSidebarOpen={isSidebarOpen} />
        <Sidebar isOpen={isSidebarOpen} onClose={handleSidebarClose} />
        <main className="lg:ml-64 pt-24 lg:pt-28">
          <div className="p-4 lg:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
              <div>
                <h1 className="text-2xl font-semibold text-foreground">Accounts</h1>
                <p className="text-muted-foreground mt-1">Manage your customer companies and relationships</p>
              </div>
              <div className="flex items-center space-x-3 mt-4 sm:mt-0">
                <Button variant="outline">
                  <Icon name="Upload" size={16} className="mr-2" />
                  Import
                </Button>
                <Button onClick={() => setIsAddModalOpen(true)}>
                  <Icon name="Plus" size={16} className="mr-2" />
                  Add Account
                </Button>
              </div>
            </div>

            {/* Search */}
            <div className="mb-4">
              <div className="relative">
                <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search accounts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <AccountsFilters
              onFiltersChange={handleFiltersChange}
              activeFilters={filters}
              resultCount={accounts?.length}
            />

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
            )}

            {loading ? (
              <div className="space-y-3">
                {[1,2,3,4,5]?.map(i => (
                  <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />
                ))}
              </div>
            ) : (
              <AccountsTable
                accounts={accounts}
                onRowClick={handleRowClick}
                onDelete={handleDeleteAccount}
                onBulkAction={() => {}} // Add this line - required prop
              />
            )}
          </div>
        </main>

        {/* Add Account Modal */}
        {isAddModalOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-foreground">Add Account</h2>
                <button onClick={() => setIsAddModalOpen(false)} className="text-muted-foreground hover:text-foreground">
                  <Icon name="X" size={20} />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Company Name *</label>
                  <input type="text" value={newAccount?.name} onChange={e => setNewAccount(p => ({...p, name: e?.target?.value}))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Industry</label>
                  <input type="text" value={newAccount?.industry} onChange={e => setNewAccount(p => ({...p, industry: e?.target?.value}))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Website</label>
                  <input type="url" value={newAccount?.website} onChange={e => setNewAccount(p => ({...p, website: e?.target?.value}))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Phone</label>
                  <input type="tel" value={newAccount?.phone} onChange={e => setNewAccount(p => ({...p, phone: e?.target?.value}))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background" />
                </div>
              </div>
              <div className="flex space-x-3 mt-6">
                <Button variant="outline" className="flex-1" onClick={() => setIsAddModalOpen(false)}>Cancel</Button>
                <Button className="flex-1" onClick={handleAddAccount} loading={saving} disabled={saving || !newAccount?.name?.trim()}>
                  {saving ? 'Saving...' : 'Add Account'}
                </Button>
              </div>
            </div>
          </div>
        )}

        <AccountDrawer
          account={selectedAccount}
          isOpen={isDrawerOpen}
          onClose={handleDrawerClose}
          onDelete={handleDeleteAccount}
          onSaved={fetchAccounts}
        />
      </div>
    </>
  );
};

export default AccountsPage;